import React, { useCallback, useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  Toolbar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatClear
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const SimpleEditor = ({
  value: propValue,
  onChange: propOnChange,
  placeholder = 'Start typing...',
  maxLength = 1000,
  error = false,
  helperText = '',
  minHeight = '300px',
}) => {
  const [charCount, setCharCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Get plain text content for character counting
  const getPlainText = useCallback((html) => {
    if (!html) return '';
    // Create a temporary div to extract plain text
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }, []);

  // Update character count
  const updateCharCount = useCallback((content) => {
    const plainText = getPlainText(content);
    setCharCount(plainText.length);
  }, [getPlainText]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (isUpdatingRef.current) return;
    
    const editor = editorRef.current;
    if (!editor) return;

    let content = editor.innerHTML;
    
    // Clean up the content but preserve meaningful HTML
    content = content
      .replace(/<div><br><\/div>/g, '<p><br></p>') // Convert empty divs to paragraphs
      .replace(/<div>/g, '<p>') // Convert divs to paragraphs
      .replace(/<\/div>/g, '</p>') // Convert closing divs to paragraphs
      .replace(/<p><\/p>/g, '<p><br></p>') // Ensure empty paragraphs have br tags
      .replace(/^<br>$/g, '<p><br></p>') // Convert standalone br to paragraph
      .trim();
    
    // If content is empty or just whitespace, set default
    if (!content || content === '<br>' || content.replace(/<[^>]*>/g, '').trim() === '') {
      content = '<p><br></p>';
    }
    
    const plainText = getPlainText(content);
    
    // Prevent input if character limit exceeded
    if (plainText.length > maxLength) {
      // Restore previous content by preventing the change
      return;
    }

    updateCharCount(content);
    
    // Debug logging to track content changes
    console.log('SimpleEditor - Content being sent:', {
      contentLength: content.length,
      plainTextLength: getPlainText(content).length,
      contentPreview: content.substring(0, 200) + (content.length > 200 ? '...' : ''),
      fullContent: content
    });
    
    if (propOnChange) {
      propOnChange(content);
    }
  }, [maxLength, updateCharCount, propOnChange, getPlainText]);

  // Handle key down for character limit
  const handleKeyDown = useCallback((e) => {
    const editor = editorRef.current;
    if (!editor) return;

    const plainText = getPlainText(editor.innerHTML);
    
    // Allow navigation keys, backspace, delete, etc.
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Enter'
    ];
    
    // Allow Ctrl/Cmd combinations
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Prevent typing if at character limit and it's a printable character
    if (plainText.length >= maxLength && !allowedKeys.includes(e.key) && e.key.length === 1) {
      e.preventDefault();
    }
  }, [maxLength, getPlainText]);

  // Toolbar commands
  const execCommand = useCallback((command, value = null) => {
    // Save current selection
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    document.execCommand(command, false, value);
    
    // Restore focus and selection
    editorRef.current?.focus();
    if (range && selection.rangeCount === 0) {
      selection.addRange(range);
    }
    
    // Trigger content update after a short delay to ensure DOM is updated
    setTimeout(() => {
      handleInput();
    }, 10);
  }, [handleInput]);

  // Handle paste events to ensure proper formatting
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    
    // Get plain text from clipboard
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    
    if (text) {
      // Insert as plain text to maintain consistency
      document.execCommand('insertText', false, text);
      setTimeout(() => {
        handleInput();
      }, 10);
    }
  }, [handleInput]);

  // Format buttons
  const formatButtons = [
    { command: 'bold', icon: FormatBold, title: 'Bold' },
    { command: 'italic', icon: FormatItalic, title: 'Italic' },
    { command: 'underline', icon: FormatUnderlined, title: 'Underline' },
    { command: 'strikeThrough', icon: StrikethroughS, title: 'Strikethrough' },
  ];

  const listButtons = [
    { command: 'insertUnorderedList', icon: FormatListBulleted, title: 'Bullet List' },
    { command: 'insertOrderedList', icon: FormatListNumbered, title: 'Numbered List' },
  ];

  const alignButtons = [
    { command: 'justifyLeft', icon: FormatAlignLeft, title: 'Align Left' },
    { command: 'justifyCenter', icon: FormatAlignCenter, title: 'Align Center' },
    { command: 'justifyRight', icon: FormatAlignRight, title: 'Align Right' },
  ];

  // Update editor content when prop value changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || isUpdatingRef.current) return;

    const currentContent = editor.innerHTML;
    let newContent = propValue || '';
    
    // Normalize the content
    if (!newContent || newContent.trim() === '' || newContent === '<br>') {
      newContent = '<p><br></p>';
    } else {
      // Clean up existing content
      newContent = newContent
        .replace(/<div><br><\/div>/g, '<p><br></p>')
        .replace(/<div>/g, '<p>')
        .replace(/<\/div>/g, '</p>')
        .replace(/<p><\/p>/g, '<p><br></p>');
    }

    if (currentContent !== newContent) {
      isUpdatingRef.current = true;
      editor.innerHTML = newContent;
      updateCharCount(newContent);
      isUpdatingRef.current = false;
    }
  }, [propValue, updateCharCount]);

  // Initial character count
  useEffect(() => {
    updateCharCount(propValue || '');
  }, [propValue, updateCharCount]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          border: error ? '1px solid #d32f2f' : '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        {/* Toolbar */}
        <Toolbar 
          variant="dense" 
          sx={{ 
            minHeight: '48px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0',
            gap: 0.5,
            px: 1
          }}
        >
          {formatButtons.map(({ command, icon: Icon, title }) => (
            <Tooltip key={command} title={title}>
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault();
                  execCommand(command);
                }}
                sx={{
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: '#1976d2',
                  },
                }}
              >
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
          
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          
          {listButtons.map(({ command, icon: Icon, title }) => (
            <Tooltip key={command} title={title}>
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault();
                  execCommand(command);
                }}
                sx={{
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: '#1976d2',
                  },
                }}
              >
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
          
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          
          {alignButtons.map(({ command, icon: Icon, title }) => (
            <Tooltip key={command} title={title}>
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault();
                  execCommand(command);
                }}
                sx={{
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    color: '#1976d2',
                  },
                }}
              >
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
          
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          
          <Tooltip title="Clear formatting">
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand('removeFormat');
              }}
              sx={{
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  color: '#1976d2',
                },
              }}
            >
              <FormatClear fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Editor */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            minHeight: minHeight,
            p: 2,
            fontSize: '14px',
            fontFamily: 'inherit',
            lineHeight: 1.5,
            outline: 'none',
            cursor: 'text',
            '&:empty::before': {
              content: `"${placeholder}"`,
              color: '#666',
              fontStyle: 'italic',
              pointerEvents: 'none',
            },
            '& p': {
              margin: '0 0 8px 0',
              fontSize: '14px !important',
              lineHeight: 1.5,
              '&:last-child': {
                marginBottom: 0,
              }
            },
            '& ul, & ol': {
              margin: '0 0 8px 0',
              paddingLeft: '20px',
              fontSize: '14px !important',
              '& li': {
                fontSize: '14px !important',
                lineHeight: 1.5,
                marginBottom: '4px',
                '&:last-child': {
                  marginBottom: 0,
                }
              }
            },
            '& strong, & em, & u, & s': {
              fontSize: 'inherit !important',
            },
            '& br': {
              lineHeight: 1.5,
            }
          }}
        />
      </Paper>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mt: 0.5,
        px: 1
      }}>
        {error && helperText && (
          <Typography 
            variant="caption" 
            color="error"
            sx={{ fontFamily: 'inherit' }}
          >
            {helperText}
          </Typography>
        )}
        <Typography 
          variant="caption" 
          color={charCount >= maxLength ? 'error' : 'textSecondary'}
          sx={{ 
            ml: 'auto',
            fontFamily: 'inherit'
          }}
        >
          {charCount}/{maxLength}
        </Typography>
      </Box>
    </Box>
  );
};

SimpleEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  minHeight: PropTypes.string,
};

export default SimpleEditor;